// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Download, QrCode } from 'lucide-react';
import { toast } from 'sonner';

/**
 * QR code for the public results link. Generated in the browser from a CDN
 * module — no image service, nothing leaves the page.
 */
export const PublicResultsQR: React.FC<{ url: string; label?: string }> = ({ url, label }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod: any = await import(/* @vite-ignore */ 'https://esm.sh/qrcode@1.5.4');
        if (cancelled || !canvasRef.current) return;
        const QR = mod.default ?? mod;
        await QR.toCanvas(canvasRef.current, url, { width: 160, margin: 1 });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = 'results-qr.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied.');
    } catch {
      toast.error('Could not copy the link.');
    }
  };

  return (
    <div className="flex items-start gap-4 rounded-lg border bg-card p-4">
      <div className="shrink-0">
        {failed ? (
          <div className="flex h-[160px] w-[160px] items-center justify-center rounded border border-dashed text-xs text-muted-foreground">
            QR unavailable offline
          </div>
        ) : (
          <canvas ref={canvasRef} className="rounded" width={160} height={160} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-semibold">
          <QrCode className="h-4 w-4" />
          {label ?? 'Public results link'}
        </p>
        <p className="mt-1 break-all text-xs text-muted-foreground">{url}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={copy}>
            <Copy className="h-3.5 w-3.5 mr-2" /> Copy link
          </Button>
          <Button size="sm" variant="outline" onClick={download} disabled={failed}>
            <Download className="h-3.5 w-3.5 mr-2" /> Download QR
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Print it, or drop it in a program — it opens the live schedule, scores and standings.
        </p>
      </div>
    </div>
  );
};

export default PublicResultsQR;
